import React, { useState, useEffect } from "react";
import api from "../api";
import { Eye, EyeOff } from "lucide-react";

export default function NuevoUsuario({ usuarioId = null }) {
  const [formData, setFormData] = useState({
    tipo_documento_id: "",
    numero_documento: "",
    nombres: "",
    apellidos: "",
    correo: "",
    telefono: "",
    direccion: "",
    departamento_id: "",
    username: "",
    password: "",
    rol_id: "",
    // Datos del primer contrato
    cargo_id: "",
    tipo_contrato_id: "",
    fecha_inicio_contrato: new Date().toISOString().split("T")[0],
    fecha_fin_contrato: "",
    salario_contrato: "",
    // Datos de contacto emergencia
    contacto_nombre: "",
    contacto_apellido: "",
    contacto_telefono: "",
    contacto_parentesco: "",
  });

  const [tiposDocumento, setTiposDocumento] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [roles, setRoles] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [tiposContrato, setTiposContrato] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // 🔹 Cargar datos iniciales
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Usamos allSettled para que una llamada fallida no cancele todas las demás
        const results = await Promise.allSettled([
          api.get('/select-data/tipos-documento'),
          api.get('/select-data/departamentos'),
          api.get('/select-data/roles'),
          api.get('/select-data/cargos'),
          api.get('/select-data/tipos-contrato'),
        ]);

        // Helper: extrae datos válidos o devuelve arreglo vacío
        const extract = (r) => {
          if (!r || r.status !== 'fulfilled' || !r.value) return [];
          // intentamos soportar response.data o response.data.data
          const d = r.value.data;
          return Array.isArray(d) ? d : (Array.isArray(d?.data) ? d.data : []);
        };

        const [td, dp, rl, cg, tc] = results.map(extract);

        setTiposDocumento(td);
        setDepartamentos(dp);
        setCargos(cg);
        setTiposContrato(tc);
        setRoles(rl);

        // Logear errores parciales para facilitar debugging en DevTools
        results.forEach((r, idx) => {
          if (r.status === 'rejected') {
            console.error(`select-data request #${idx} failed:`, r.reason);
          }
        });

        // Si estamos en modo edición, cargar datos del usuario
        if (usuarioId) {
          const res = await api.get(`/usuarios/${usuarioId}`);
          const empleadoData = res.data.usuario.empleado;
          const ultimoContrato = empleadoData.periodos_contrato && empleadoData.periodos_contrato.length > 0
            ? empleadoData.periodos_contrato[empleadoData.periodos_contrato.length - 1]
            : {};

          setFormData({
            ...empleadoData,
            username: res.data.usuario.username,
            rol_id: res.data.usuario.rol_id,
            password: "", // No cargar la contraseña
            // Datos del último contrato
            cargo_id: ultimoContrato.cargo_id || "",
            tipo_contrato_id: ultimoContrato.tipo_contrato_id || "",
            fecha_inicio_contrato: ultimoContrato.fecha_inicio || "",
            fecha_fin_contrato: ultimoContrato.fecha_fin || "",
            salario_contrato: ultimoContrato.salario || "",
            // Datos de contacto emergencia
            contacto_nombre: empleadoData.contactoEmergencia?.nombre || "",
            contacto_apellido: empleadoData.contactoEmergencia?.apellido || "",
            contacto_telefono: empleadoData.contactoEmergencia?.telefono || "",
            contacto_parentesco: empleadoData.contactoEmergencia?.parentesco || "",
          });
          setEditMode(true);
        }
      } catch (err) {
        // Esto debería ocurrir muy rara vez porque usamos allSettled
        console.error("Error inesperado cargando datos iniciales:", err);
        alert("No se pudieron cargar los datos iniciales. Revisa la consola para más detalles.");
      }
    };
    fetchData();
  }, [usuarioId]);

  // 🔹 Filtros visuales (solo presentación)
  const formatTelefono = (tel) => {
    if (!tel) return "";
    const clean = tel.replace(/\D/g, "");
    if (clean.length <= 3) return clean;
    if (clean.length <= 6) return `(${clean.slice(0, 3)}) ${clean.slice(3)}`;
    return `(${clean.slice(0, 3)}) ${clean.slice(3, 6)}-${clean.slice(6, 10)}`;
  };

  const formatDocumento = (num) => {
    if (!num) return "";
    return num.replace(/\D/g, "").replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  };

  const formatSalario = (sal) => {
    if (!sal) return "";
    const clean = sal.toString().replace(/\D/g, "");
    return `$ ${Number(clean).toLocaleString("es-CO")}`;
  };

  // 🔹 Manejar cambios en inputs
const handleChange = (e) => {
  const { name, value } = e.target;
  
  // Campos donde SÍ queremos eliminar puntos
  const camposSinPuntos = ["numero_documento", "username", "salario_contrato", "telefono", "contacto_telefono"];
  
  let newValue = value;
  if (camposSinPuntos.includes(name)) {
    newValue = value.replace(/\./g, "");
  }
  
  let updatedData = { ...formData, [name]: newValue };

  // Si cambia el documento → actualizar username y password automáticamente
  if (name === "numero_documento" && !editMode) {
    updatedData.username = newValue;
    updatedData.password = newValue;
  }

  setFormData(updatedData);
};

  // 🔹 Enviar datos
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const token = localStorage.getItem("token");

    try {
      let res;
      if (editMode) {
        // 🔄 Actualizar
        console.log("Enviando datos para actualizar usuario:", formData);
        res = await api.put(
          `/usuarios/${usuarioId}/actualizar`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log("Respuesta del servidor (actualización):", res.data);
        alert("✅ Usuario actualizado correctamente");
      } else {
        // 🆕 Crear
        console.log("Enviando datos para crear usuario:", formData);
        res = await api.post(`/usuarios`, {
          ...formData,
          salario_contrato: formData.salario_contrato.replace(/[^0-9]/g, ''), // Limpiar formato de salario
        }, {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log("Respuesta del servidor (creación):", res.data);
        alert("✅ Usuario creado correctamente");
      }

      console.log("Respuesta backend:", res.data);

      // 🔄 Limpiar formulario
      setFormData({
        tipo_documento_id: "",
        numero_documento: "",
        nombres: "",
        apellidos: "",
        fecha_nacimiento: "",
        correo: "",
        telefono: "",
        direccion: "",
        departamento_id: "",
        username: "",
        password: "",
        rol_id: "",
        cargo_id: "",
        tipo_contrato_id: "",
        fecha_inicio_contrato: new Date().toISOString().split("T")[0],
        fecha_fin_contrato: "",
        salario_contrato: "",
        contacto_nombre: "",
        contacto_apellido: "",
        contacto_telefono: "",
        contacto_parentesco: "",
      });
      setEditMode(false);
    } catch (err) {
      console.error("❌ Error al guardar usuario:", err.response?.data || err);
      alert(err.response?.data?.message || "Error al guardar usuario");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 bg-white rounded-2xl shadow-lg">
      <h2 className="text-2xl font-bold mb-4">
        {editMode ? "✏️ Editar Usuario" : "🧍 Registrar nuevo usuario"}
      </h2>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* 🔸 Sección de datos del empleado */}
        <div className="md:col-span-2 mb-2 border-b pb-1 font-semibold text-gray-600">
          Datos del empleado
        </div>

        <select
          name="tipo_documento_id"
          value={formData.tipo_documento_id}
          onChange={handleChange}
          required
          className="border rounded-lg p-2"
        >
          <option value="">Seleccionar tipo de documento</option>
          {tiposDocumento.map((t) => (
            <option key={t.id} value={t.id}>
              {t.nombre}
            </option>
          ))}
        </select>

        {/* Documento con formato visual */}
        <input
          type="text"
          name="numero_documento"
          placeholder="Número de documento"
          value={formatDocumento(formData.numero_documento)}
          onChange={handleChange}
          required
          className="border rounded-lg p-2"
        />

        <input
          type="text"
          name="nombres"
          placeholder="Nombres"
          value={formData.nombres}
          onChange={handleChange}
          required
          className="border rounded-lg p-2"
        />
        <input
          type="text"
          name="apellidos"
          placeholder="Apellidos"
          value={formData.apellidos}
          onChange={handleChange}
          required
          className="border rounded-lg p-2"
        />

        {/* Fecha de nacimiento */}
        <label className="flex flex-col">
          <span className="text-sm text-gray-600 mb-1">📅 Fecha de nacimiento</span>
          <input
            type="date"
            name="fecha_nacimiento"
            value={formData.fecha_nacimiento}
            onChange={handleChange}
            className="border rounded-lg p-2"
          />
        </label>

        <input
          type="email"
          name="correo"
          placeholder="Correo electrónico"
          value={formData.correo}
          onChange={handleChange}
          required
          className="border rounded-lg p-2"
        />

        {/* Teléfono con formato visual */}
        <input
          type="text"
          name="telefono"
          placeholder="Teléfono"
          value={formatTelefono(formData.telefono)}
          onChange={handleChange}
          className="border rounded-lg p-2"
        />

        <input
          type="text"
          name="direccion"
          placeholder="Dirección"
          value={formData.direccion}
          onChange={handleChange}
          className="border rounded-lg p-2"
        />

        <select
          name="departamento_id"
          value={formData.departamento_id}
          onChange={handleChange}
          required
          className="border rounded-lg p-2"
        >
          <option value="">Seleccionar departamento</option>
          {departamentos.map((d) => (
            <option key={d.id} value={d.id}>
              {d.nombre}
            </option>
          ))}
        </select>

        <div className="md:col-span-2 mt-2 border-b pb-1 font-semibold text-gray-600">
          Datos del primer contrato
        </div>

        <select
          name="cargo_id"
          value={formData.cargo_id}
          onChange={handleChange}
          required
          className="border rounded-lg p-2"
        >
          <option value="">Seleccionar cargo</option>
          {cargos.map((c) => (
            <option key={c.id} value={c.id}>
              {c.nombre}
            </option>
          ))}
        </select>

        <select
          name="tipo_contrato_id"
          value={formData.tipo_contrato_id}
          onChange={handleChange}
          required
          className="border rounded-lg p-2"
        >
          <option value="">Seleccionar tipo de contrato</option>
          {tiposContrato.map((tc) => (
            <option key={tc.id} value={tc.id}>
              {tc.nombre}
            </option>
          ))}
        </select>

        <label className="flex flex-col">
          <span className="text-sm text-gray-600 mb-1">📆 Fecha de inicio contrato</span>
          <input
            type="date"
            name="fecha_inicio_contrato"
            value={formData.fecha_inicio_contrato}
            onChange={handleChange}
            required
            className="border rounded-lg p-2"
          />
        </label>

        <label className="flex flex-col">
          <span className="text-sm text-gray-600 mb-1">🗓️ Fecha de fin contrato (opcional)</span>
          <input
            type="date"
            name="fecha_fin_contrato"
            value={formData.fecha_fin_contrato}
            onChange={handleChange}
            className="border rounded-lg p-2"
          />
        </label>

        <input
          type="text"
          name="salario_contrato"
          placeholder="Salario del contrato"
          value={formatSalario(formData.salario_contrato)}
          onChange={handleChange}
          required
          className="border rounded-lg p-2"
        />

        {/* 🔸 Sección de datos del usuario */}
        <div className="md:col-span-2 mt-2 border-b pb-1 font-semibold text-gray-600">
          Datos del usuario
        </div>

        {/* Username formateado como documento */}
        <input
          type="text"
          name="username"
          placeholder="Nombre de usuario"
          value={formatDocumento(formData.username)}
          readOnly
          className="border rounded-lg p-2 bg-cart cursor-not-allowed"
        />

        {/* Contraseña con icono */}
        <div className="relative flex items-center">
          <input
            type={showPassword ? "text" : "password"}
            name="password"
            placeholder={editMode ? "Dejar vacío para no cambiar" : "Contraseña (automática)"}
            value={formData.password}
            onChange={handleChange}
            readOnly={!editMode}
            className={`border rounded-lg p-2 w-full pr-10 ${
              !editMode ? "bg-cart cursor-not-allowed" : ""
            }`}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 text-gray-500 hover:text-gray-700"
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>

        <select
          name="rol_id"
          value={formData.rol_id}
          onChange={handleChange}
          required
          className="border rounded-lg p-2"
        >
          <option value="">Seleccionar rol</option>
          {roles.map((r) => (
            <option key={r.id} value={r.id}>
              {r.nombre}
            </option>
          ))}
        </select>

        {/* 🔸 Sección de datos de contacto emergencia */}
        <div className="md:col-span-2 mt-2 border-b pb-1 font-semibold text-gray-600">
          Datos de contacto emergencia
        </div>

        <input
          type="text"
          name="contacto_nombre"
          placeholder="Nombre del contacto"
          value={formData.contacto_nombre}
          onChange={handleChange}
          className="border rounded-lg p-2"
        />

        <input
          type="text"
          name="contacto_apellido"
          placeholder="Apellido del contacto"
          value={formData.contacto_apellido}
          onChange={handleChange}
          className="border rounded-lg p-2"
        />

        <input
          type="text"
          name="contacto_telefono"
          placeholder="Teléfono del contacto"
          value={formatTelefono(formData.contacto_telefono)}
          onChange={handleChange}
          className="border rounded-lg p-2"
        />

        <input
          type="text"
          name="contacto_parentesco"
          placeholder="Parentesco (ej: Padre, Madre, Hermano, Esposo/a, Hijo/a)"
          value={formData.contacto_parentesco}
          onChange={handleChange}
          className="border rounded-lg p-2"
        />

        <div className="md:col-span-2 flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className={`px-6 py-2 rounded-lg font-semibold text-white ${
              loading ? "bg-gray-400" : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {loading ? "Guardando..." : editMode ? "Actualizar usuario" : "Guardar usuario"}
          </button>
        </div>
      </form>
    </div>
  );
}
